import { useTranslation } from 'react-i18next';
/**
 * TeamMemberRateHistory.js
 *
 * Per-project per-employee rate history modal. Rendered from a row in
 * the project's "Team & Rates" editor. Lets an admin see the timeline
 * of rates, add a new effective-dated rate, and patch / delete past
 * entries.
 *
 * Network shape mirrors the backend:
 *   GET    /api/projects/{pid}/team/{eid}/rate-history
 *   POST   /api/projects/{pid}/team/{eid}/rate-history
 *   GET    /api/projects/{pid}/team/{eid}/rate-history/impact
 *   PATCH  /api/projects/{pid}/team/{eid}/rate-history/{rid}
 *   DELETE /api/projects/{pid}/team/{eid}/rate-history/{rid}
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import { Badge } from './ui/badge';
import { History, Plus, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const CURRENCIES = ['EUR', 'PLN', 'GBP', 'USD'];

export default function TeamMemberRateHistory({
  open, onClose, projectId, employeeId, employeeName, isAdmin,
  onUpdated, // (newCurrentRate, newCurrency, newHistory) => void
}) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [currentRate, setCurrentRate] = useState(null);
  const [currency, setCurrency] = useState('PLN');
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ rate: '', currency: 'PLN', effective_from: '', note: '' });
  const [impact, setImpact] = useState(null);

  const reload = async () => {
    if (!projectId || !employeeId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/projects/${projectId}/team/${employeeId}/rate-history`);
      setHistory(r.data.history || []);
      setCurrentRate(r.data.current_rate);
      setCurrency(r.data.currency || 'PLN');
      setForm((f) => ({ ...f, currency: r.data.currency || f.currency || 'PLN' }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load rate history');
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) { reload(); setShowAdd(false); setImpact(null); } }, [open, projectId, employeeId]); // eslint-disable-line

  const checkImpact = async (effectiveFrom) => {
    if (!effectiveFrom) { setImpact(null); return; }
    try {
      const r = await axios.get(
        `${API}/api/projects/${projectId}/team/${employeeId}/rate-history/impact`,
        { params: { effective_from: effectiveFrom } },
      );
      setImpact(r.data);
    } catch (_e) { setImpact(null); }
  };

  const handleAdd = async () => {
    if (!form.rate || !form.effective_from || !form.note?.trim()) {
      toast.error(t('auto.misc.rateEffectiveDateAndNoteAre'));
      return;
    }
    try {
      const r = await axios.post(
        `${API}/api/projects/${projectId}/team/${employeeId}/rate-history`,
        {
          rate: parseFloat(form.rate),
          currency: form.currency,
          effective_from: form.effective_from,
          note: form.note.trim(),
        },
      );
      setHistory(r.data.history || []);
      setCurrentRate(r.data.current_rate);
      setCurrency(r.data.currency);
      setShowAdd(false);
      setForm({ rate: '', currency: r.data.currency || 'PLN', effective_from: '', note: '' });
      setImpact(null);
      onUpdated?.(r.data.current_rate, r.data.currency, r.data.history || []);
      toast.success(t('auto.misc.rateAdded'));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add rate');
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm(t('auto.misc.deleteThisRateEntryPastTime'))) return;
    try {
      const r = await axios.delete(
        `${API}/api/projects/${projectId}/team/${employeeId}/rate-history/${entryId}`,
      );
      setHistory(r.data.history || []);
      setCurrentRate(r.data.current_rate);
      onUpdated?.(r.data.current_rate, r.data.currency, r.data.history || []);
      toast.success(t('auto.misc.rateEntryDeleted'));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const today = new Date().toISOString().slice(0, 10);
  const isBackdated = form.effective_from && form.effective_from < today;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="rate-history-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-4 h-4" /> Rate history — {employeeName || 'Team member'}
          </DialogTitle>
          <DialogDescription>
            Each entry applies from <em>{t('auto.misc.effectiveFrom')}</em> until the next entry kicks in. Labour
            cost and employee invoices use the rate effective on the work date.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-500">{t('auto.misc.currentRate')}</span>
            <span className="font-semibold" data-testid="rate-history-current">
              {currentRate != null ? `${currentRate.toFixed(2)} ${currency}/h` : '—'}
            </span>
          </div>

          {loading ? (
            <div className="text-sm text-slate-400 py-4">{t('auto.misc.loading')}</div>
          ) : (
            <div className="border rounded">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left px-3 py-2">{t('auto.misc.from')}</th>
                    <th className="text-left px-3 py-2">{t('auto.misc.to')}</th>
                    <th className="text-right px-3 py-2">{t('auto.misc.rate')}</th>
                    <th className="text-left px-3 py-2">{t('auto.misc.note')}</th>
                    {isAdmin && <th className="w-8"></th>}
                  </tr>
                </thead>
                <tbody>
                  {history.length === 0 && (
                    <tr><td colSpan={isAdmin ? 5 : 4} className="text-center text-slate-400 py-4">{t('auto.misc.noHistory')}</td></tr>
                  )}
                  {[...history].reverse().map((h) => {
                    const today = new Date().toISOString().slice(0, 10);
                    const isFuture = h.effective_from > today;
                    const isCurrent = !h.effective_to && !isFuture;
                    return (
                    <tr key={h.id} className="border-t" data-testid={`rate-history-row-${h.id}`}>
                      <td className="px-3 py-2 font-mono text-xs">{h.effective_from}</td>
                      <td className="px-3 py-2 font-mono text-xs">
                        {h.effective_to ? h.effective_to : (
                          isCurrent
                            ? <Badge variant="outline" className="text-[10px] bg-green-50 text-green-700 border-green-200">current</Badge>
                            : <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200">scheduled</Badge>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold">
                        {Number(h.rate).toFixed(2)} {h.currency}
                      </td>
                      <td className="px-3 py-2 text-slate-600">{h.note || ''}</td>
                      {isAdmin && (
                        <td className="px-1 py-2">
                          <Button
                            type="button" size="icon" variant="ghost"
                            onClick={() => handleDelete(h.id)}
                            disabled={history.length <= 1}
                            data-testid={`rate-history-delete-${h.id}`}
                          ><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
                        </td>
                      )}
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {isAdmin && !showAdd && (
            <Button type="button" size="sm" variant="outline" onClick={() => setShowAdd(true)}
              data-testid="rate-history-add-btn">
              <Plus className="w-4 h-4 mr-1" /> {t('auto.misc.addNewRate')}
            </Button>
          )}

          {isAdmin && showAdd && (
            <div className="border rounded p-3 space-y-3 bg-slate-50">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div>
                  <Label className="text-xs">{t('auto.misc.newRate')}</Label>
                  <Input type="number" step="0.01" min="0" value={form.rate}
                    onChange={(e) => setForm(prev => ({ ...prev, rate: e.target.value  }))}
                    data-testid="rate-history-form-rate" />
                </div>
                <div>
                  <Label className="text-xs">{t('auto.misc.currency')}</Label>
                  <Select value={form.currency} onValueChange={(v) => setForm(prev => ({ ...prev, currency: v  }))}>
                    <SelectTrigger data-testid="rate-history-form-currency"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">{t('auto.misc.effectiveFrom2')}</Label>
                  <Input type="date" value={form.effective_from}
                    onChange={(e) => { setForm(prev => ({ ...prev, effective_from: e.target.value  })); checkImpact(e.target.value); }}
                    data-testid="rate-history-form-effective-from" />
                </div>
              </div>
              <div>
                <Label className="text-xs">{t('auto.misc.noteRequiredReasonForTheChange')}</Label>
                <Textarea rows={2} value={form.note}
                  placeholder="e.g. annual raise, promotion to senior, project-specific adjustment…"
                  onChange={(e) => setForm(prev => ({ ...prev, note: e.target.value  }))}
                  data-testid="rate-history-form-note" />
              </div>

              {isBackdated && impact && (
                <div className="flex items-start gap-2 p-2 rounded bg-amber-50 border border-amber-200 text-amber-900 text-xs"
                  data-testid="rate-history-backdate-warning">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <div>
                    <div className="font-semibold">{t('auto.misc.backdatingImpact')}</div>
                    <div>
                      {impact.time_entries_affected} time entr{impact.time_entries_affected === 1 ? 'y' : 'ies'} on/after{' '}
                      <span className="font-mono">{impact.effective_from}</span> will re-price.{' '}
                      {impact.draft_invoices_affected} draft invoice{impact.draft_invoices_affected === 1 ? '' : 's'} affected.
                      Finalised invoices keep their snapshot rate.
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" size="sm" onClick={() => { setShowAdd(false); setImpact(null); }} data-testid="btn-cancel">{t('auto.misc.cancel')}</Button>
                <Button type="button" size="sm" onClick={handleAdd} data-testid="rate-history-save-btn">{t('auto.misc.save')}</Button>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} data-testid="rate-history-close-btn">{t('auto.misc.close')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
